import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, CreditCard, Loader2 } from "lucide-react";
import MarketingLayout from "@/components/public/MarketingLayout";
import PublicBreadcrumb from "@/components/public/PublicBreadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import { createCheckoutSession, fetchBillingPlans } from "@/lib/billing";
import { audiencePlans, faqItems } from "@/content/siteContent";

function PlanCard({ plan, onCheckout, checkoutLoading, authenticated }) {
  const isCheckout = plan.ctaMode === "checkout";
  const isSignup = plan.ctaMode === "signup";
  const isContact = plan.ctaMode === "contact";

  return (
    <div className="flex h-full flex-col rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-display text-3xl font-black text-slate-950">{plan.name}</div>
          <div className="mt-2 text-sm text-slate-500">{plan.description}</div>
        </div>
        {plan.badge && <span className="rounded-full bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">{plan.badge}</span>}
      </div>
      <div className="mt-6 font-display text-4xl font-black text-slate-950">{plan.priceLabel}</div>
      <div className="mt-6 space-y-3 text-sm text-slate-600">
        {plan.highlights.map((highlight) => (
          <div key={highlight} className="flex gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{highlight}</div>
        ))}
      </div>
      <div className="mt-8">
        {isCheckout && authenticated ? (
          <Button onClick={() => onCheckout(plan.code)} disabled={checkoutLoading === plan.code} className="h-11 w-full rounded-full bg-[#0f172a] hover:bg-slate-800">
            {checkoutLoading === plan.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {plan.ctaLabel}
          </Button>
        ) : isCheckout ? (
          <Button asChild className="h-11 w-full rounded-full bg-[#0f172a] hover:bg-slate-800">
            <Link to="/auth/login">Accedi per attivare</Link>
          </Button>
        ) : isSignup ? (
          <Button asChild className="h-11 w-full rounded-full bg-[#0f172a] hover:bg-slate-800">
            <Link to="/contact">Richiedi attivazione</Link>
          </Button>
        ) : isContact ? (
          <Button asChild variant="outline" className="h-11 w-full rounded-full border-slate-300 bg-white">
            <Link to="/contact">{plan.ctaLabel}</Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(audiencePlans);
  const [checkoutLoading, setCheckoutLoading] = useState("");

  useEffect(() => {
    fetchBillingPlans().then((data) => {
      if (data?.audiences) setPlans(data.audiences);
    }).catch(() => {
      // Keep local fallback catalog.
    });
  }, []);

  const tabDefault = useMemo(() => user?.role === "company_user" ? "companies" : "consultants", [user]);

  const handleCheckout = async (planCode) => {
    setCheckoutLoading(planCode);
    try {
      const session = await createCheckoutSession(planCode);
      if (session?.checkout_url) {
        window.location.href = session.checkout_url;
        return;
      }
      toast.error("Checkout Stripe non disponibile.");
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/auth/login");
        return;
      }
      toast.error(getApiErrorMessage(err, "Errore attivazione Stripe"));
    } finally {
      setCheckoutLoading("");
    }
  };

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-7xl px-5 pb-12 pt-16 md:px-8 md:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <div className="text-left"><PublicBreadcrumb currentLabel="Prezzi" /></div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-700">Prezzi e abbonamenti</div>
          <h1 className="mt-4 font-display text-5xl font-black leading-tight text-slate-950 md:text-6xl">Un listino chiaro per trasformare la compliance in un servizio ricorrente.</h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">Stripe gestisce il checkout e gli abbonamenti. Audit Pro gestisce tenant, workflow e controllo operativo.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-12 md:px-8">
        <Tabs defaultValue={tabDefault} className="w-full">
          <TabsList className="mx-auto grid w-full max-w-md grid-cols-2 rounded-full bg-slate-200/70 p-1">
            <TabsTrigger value="consultants" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Consulenti</TabsTrigger>
            <TabsTrigger value="companies" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Aziende</TabsTrigger>
          </TabsList>
          {Object.entries(plans).map(([key, value]) => (
            <TabsContent key={key} value={key} className="mt-8">
              <div className="grid gap-6 lg:grid-cols-3">
                {value.map((plan) => (
                  <PlanCard key={plan.code} plan={plan} onCheckout={handleCheckout} checkoutLoading={checkoutLoading} authenticated={!!user} />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          {faqItems.map((item) => (
            <div key={item.q} className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="font-display text-2xl font-bold text-slate-950">{item.q}</div>
              <div className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</div>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
